import {
    WEBSITE_PLATFORMS,
    platformLabel,
    type WebsitePlatform,
} from '@flexbyt/shared';

/**
 * The website platforms, and exactly how far the integration goes on each.
 *
 * ---------------------------------------------------------------------------
 * "Works on Wix" and "native Wix integration" are different claims
 * ---------------------------------------------------------------------------
 * The brief asks for these to be kept apart, and the difference is real enough
 * to be worth a type rather than an adjective. Three genuinely different things
 * are true of different platforms:
 *
 *   `app`        There is a ReviewsJet application in the platform's own
 *                marketplace. Installing it signs you in, and the platform
 *                tells us who you are — Wix by `instanceId`, Webflow through
 *                OAuth. This is what "native integration" means, and only Wix
 *                and Webflow have it.
 *
 *   `plugin`     There is a ReviewsJet plugin, but the account is an ordinary
 *                ReviewsJet account. WordPress: the plugin registers the
 *                `[rj_slider]` shortcodes, which exist because a `<script>` tag
 *                pasted into a Custom HTML block is stripped by a great many
 *                themes and security plugins.
 *
 *   `embed`      The widget is a snippet you paste. It works, completely — but
 *                calling it an integration would be dressing up "this is HTML
 *                and HTML works everywhere".
 *
 * A page that says "native Squarespace integration" is a page whose first
 * instruction is "paste this code", and the visitor notices.
 *
 * ---------------------------------------------------------------------------
 * There is no Shopify entry
 * ---------------------------------------------------------------------------
 * ReviewsJet does not support Shopify. It is commented out of
 * `WEBSITE_PLATFORMS` in the shared package, so it cannot be named here even by
 * accident — `WebsitePlatform` does not include it, and the exhaustive record
 * below would fail to compile if it did.
 */

export type IntegrationDepth = 'app' | 'plugin' | 'embed';

export type PlatformContent = {
  platform: WebsitePlatform;
  /** The URL segment: `/reviews/google/wix`. */
  slug: string;
  /** The platform's own spelling. */
  name: string;
  depth: IntegrationDepth;
  /** The honest one-liner. Never stronger than `depth` supports. */
  claim: string;
  /** How the widget actually gets onto the site. */
  install: string;
  /** Where the marketplace listing is, when there is one. */
  marketplaceUrl?: string;
};

/**
 * Every platform the product knows about, with its real integration depth.
 *
 * Exhaustive over `WebsitePlatform` by construction: adding a platform to the
 * shared list without deciding what is true about it here is a type error,
 * which is the point. A marketing site that silently omits a platform the
 * product supports is a marketing site that leaves traffic on the table.
 */
export const PLATFORM_CONTENT: Record<WebsitePlatform, PlatformContent> = {
  wix: {
    platform: 'wix',
    slug: 'wix',
    name: 'Wix',
    depth: 'app',
    claim: 'Native Wix app',
    install:
      'Install ReviewsJet from the Wix App Market and it signs you in from your Wix dashboard. Widgets are added in the Editor as a Custom Element.',
    marketplaceUrl: 'https://www.wix.com/app-market/reviewsjet',
  },
  webflow: {
    platform: 'webflow',
    slug: 'webflow',
    name: 'Webflow',
    depth: 'app',
    claim: 'Native Webflow app',
    install:
      'Install the ReviewsJet app from Webflow, then drop an Embed element where the reviews should go and publish.',
    marketplaceUrl: 'https://webflow.com/apps/detail/reviewsjet',
  },
  wordpress: {
    platform: 'wordpress',
    slug: 'wordpress',
    name: 'WordPress',
    depth: 'plugin',
    claim: 'Official WordPress plugin',
    install:
      'Install the ReviewsJet plugin from the WordPress directory and place a shortcode — [rj_slider], [rj_masonry] and so on — wherever the reviews belong.',
    marketplaceUrl: 'https://wordpress.org/plugins/reviewsjet',
  },
  /*
    An embed until the Framer plugin is approved for the Marketplace — then
    `depth: 'plugin'` with `marketplaceUrl: FRAMER_MARKETPLACE_URL`. Saying
    "plugin" before anybody can install it would be the stronger claim the
    type exists to stop.
  */
  framer: {
    platform: 'framer',
    slug: 'framer',
    name: 'Framer',
    depth: 'embed',
    claim: 'Works on Framer',
    install:
      'Add an Embed component where the reviews should appear, paste the embed code, and publish.',
  },
  squarespace: {
    platform: 'squarespace',
    slug: 'squarespace',
    name: 'Squarespace',
    depth: 'embed',
    claim: 'Works on Squarespace',
    install:
      'Add a Code block where the reviews should appear and paste the embed code. Code blocks only render on the published site.',
  },
  woocommerce: {
    platform: 'woocommerce',
    slug: 'woocommerce',
    name: 'WooCommerce',
    depth: 'plugin',
    claim: 'Works on WooCommerce through the WordPress plugin',
    install:
      'WooCommerce runs on WordPress, so the ReviewsJet plugin and its shortcodes work exactly as they do on any WordPress site.',
  },
  pixpa: {
    platform: 'pixpa',
    slug: 'pixpa',
    name: 'Pixpa',
    depth: 'embed',
    claim: 'Works on Pixpa',
    install:
      'Add a Custom HTML section to the page and paste the embed code, then publish.',
  },
  godaddy: {
    platform: 'godaddy',
    slug: 'godaddy',
    name: 'GoDaddy',
    depth: 'embed',
    claim: 'Works on GoDaddy Website Builder',
    install:
      'Add an HTML section to the page and paste the embed code.',
  },
  other: {
    platform: 'other',
    slug: 'html',
    name: 'any website',
    depth: 'embed',
    claim: 'Works on any website',
    install:
      'Paste the embed code into your HTML where the reviews should appear. It is a script tag and a custom element — nothing else is required.',
  },
};

/**
 * The platforms worth their own `source × platform` pages.
 *
 * Not all of them. `other` is a catch-all rather than something anybody
 * searches for, and GoDaddy and Pixpa have too little search behind them to
 * carry a page that is genuinely about them — which is the line the brief draws
 * against thin programmatic pages. The four below are the ones where "Google
 * reviews for X" is a phrase real people type.
 */
export const SEO_PLATFORMS: WebsitePlatform[] = [
  'wix',
  'webflow',
  'wordpress',
  'squarespace',
];

export function platformContentBySlug(slug: string): PlatformContent | undefined {
  return Object.values(PLATFORM_CONTENT).find((entry) => entry.slug === slug);
}

/** True when the platform has a real app or plugin behind it. */
export function isNativeIntegration(platform: WebsitePlatform): boolean {
  return PLATFORM_CONTENT[platform].depth !== 'embed';
}

/** The product's own label, so the two never disagree on spelling. */
export function productPlatformLabel(platform: WebsitePlatform): string {
  return platformLabel(platform);
}

/** Guards against a platform being added to the product and missed here. */
export const KNOWN_PLATFORMS = WEBSITE_PLATFORMS;
